import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PartyPopper, Check } from "lucide-react";
import api, { money, fmtErr } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Upsell() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/orders/${orderId}`).then((r) => setOrder(r.data));
  useEffect(() => { window.scrollTo(0, 0); load(); }, [orderId]);

  if (!order) return <div className="min-h-screen grid place-items-center text-stone-400">Loading…</div>;

  const upsells = order.available_upsells || [];
  const current = upsells[0];

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/upsell`, { upsell_id: current.id });
      setOrder(data);
      toast.success("Added to your order — no extra shipping! 🎉");
      const remaining = data.available_upsells || [];
      if (remaining.length === 0) nav(`/thank-you/${orderId}`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const decline = () => nav(`/thank-you/${orderId}`);

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white"><div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-center"><Logo className="text-2xl" /></div></header>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="rounded-3xl bg-white border border-stone-100 friib-shadow p-8 text-center mb-6">
          <div className="w-16 h-16 mx-auto grid place-items-center rounded-full mb-4" style={{ background: "var(--mint-light)" }}>
            <PartyPopper size={30} style={{ color: "var(--mint)" }} />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-stone-900">Your Friib is secured 🎉</h1>
          <p className="text-stone-500 mt-1">You just claimed <b className="text-stone-800">{order.product_name}</b>. Before you go…</p>
        </motion.div>

        {current ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="rounded-3xl bg-white border-2 border-[var(--friib)] friib-shadow-hover overflow-hidden" data-testid="upsell-offer">
            <div className="px-6 pt-6"><span className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--friib)]">Complete your routine</span></div>
            <div className="p-6 flex flex-col sm:flex-row gap-6 items-center">
              <img src={current.image} alt={current.name} className="w-40 h-40 object-cover rounded-2xl" />
              <div className="flex-1 text-center sm:text-left">
                <h2 className="font-display text-2xl font-extrabold text-stone-900 mb-1">{current.name}</h2>
                <p className="text-stone-500 mb-3">{current.description}</p>
                <div className="flex items-center gap-3 justify-center sm:justify-start">
                  <span className="text-stone-400 line-through text-lg">{money(current.regular_price)}</span>
                  <span className="font-display text-3xl font-extrabold" style={{ color: "var(--friib)" }}>{money(current.upsell_price)} today</span>
                </div>
              </div>
            </div>
            <div className="p-6 pt-0 space-y-3">
              <button onClick={accept} disabled={busy} data-testid="accept-upsell-btn"
                className="w-full py-4 rounded-full font-extrabold text-white text-lg transition-transform hover:scale-[1.01] disabled:opacity-60" style={{ background: "var(--friib)" }}>
                Yes — Add It To My Order (1-click)
              </button>
              <button onClick={decline} data-testid="decline-upsell-btn" className="w-full py-3 rounded-full font-bold text-stone-500 hover:text-stone-900">No thanks, continue</button>
              <p className="text-center text-xs text-stone-400 flex items-center justify-center gap-1"><Check size={13} /> No need to re-enter payment · ships free with your Friib</p>
            </div>
          </motion.div>
        ) : (
          <div className="text-center"><button onClick={decline} className="px-8 py-3 rounded-full font-bold text-white" style={{ background: "var(--friib)" }}>Continue</button></div>
        )}

        {order.upsells?.length > 0 && (
          <p className="mt-5 text-center text-sm text-emerald-600 font-semibold">Added: {order.upsells.map((u) => u.name).join(", ")}</p>
        )}
      </div>
    </div>
  );
}
